/*
 * Copyright (c) 2024-2026 Nodals AI Ltd
 * All rights reserved.
 */

import { Logger } from '../logger';
import { generateUUID } from '../util';

const logger = Logger.getInstance();

/**
 * The pcids/bw/bh values exactly as the select request payload carried them,
 * captured at payload-build time so impressions attributed to the auction can
 * report the same values the selection was made with.
 */
type AuctionLockedFacts = {
  pcids: string[];
  bw: number;
  bh: number;
};

/**
 * A single selection auction. Owns the opportunity shared by every placement
 * participating in that auction; opportunities minted outside an auction
 * (render-minted, advertorial) have no Auction and therefore no locked facts.
 */
class Auction {
  private _opportunity: Opportunity;
  private _lockedFacts?: AuctionLockedFacts;

  constructor() {
    this._opportunity = new Opportunity(generateUUID(), this);
  }

  public get opportunity(): Opportunity {
    return this._opportunity;
  }

  public get lockedFacts(): AuctionLockedFacts | undefined {
    return this._lockedFacts;
  }

  /**
   * Records the facts the select payload carried. Write-once: the first call
   * wins and later calls are ignored. The pcids array is copied so callers
   * cannot mutate the snapshot afterwards.
   */
  public lockFacts(facts: AuctionLockedFacts): void {
    if (this._lockedFacts) {
      return;
    }
    this._lockedFacts = { pcids: [...facts.pcids], bw: facts.bw, bh: facts.bh };
  }
}

class Opportunity {
  private _id: string;
  private creationTime: number = 0;
  private _auction?: Auction;

  constructor(id: string, auction?: Auction) {
    this._id = id;
    this._auction = auction;
    this.creationTime = Date.now();

    logger.debug(`Opportunity created with ID: ${this._id}`);
  }

  public get id(): string {
    return this._id;
  }

  /**
   * The auction this opportunity was minted for, or undefined when it was
   * minted outside an auction (e.g. for an untracked render).
   */
  public get auction(): Auction | undefined {
    return this._auction;
  }

  /**
   * Returns the age of the opportunity in milliseconds.
   */
  public get age(): number {
    return Date.now() - this.creationTime;
  }

}

export { Auction, Opportunity };
export type { AuctionLockedFacts };
